import React, { useMemo, useState } from "react";
import { ResponsiveContainer, ComposedChart, LineChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { formatMoney, formatMoney2, formatNumber, formatPct, formatApiErrorDetail } from "../../lib/api";
import api from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { Hist } from "../HistRef";
import { EmployeeReportButton, mediaFarmacia } from "../../pages/Ventas";

const dec2 = (v) => (v ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const KPIS = [
  { k: "facturacion", label: "Facturación", fmt: formatMoney },
  { k: "facturacion_parafarmacia", label: "Parafarmacia", fmt: formatMoney },
  { k: "pct_parafarmacia", label: "% parafarmacia", fmt: formatPct, pp: true },
  { k: "tickets", label: "Operaciones", fmt: formatNumber, tickets: true },
  { k: "ticket_medio", label: "Ticket medio", fmt: formatMoney2, tickets: true },
  { k: "ticket_medio_mixta", label: "TM venta mixta", fmt: formatMoney2, tickets: true },
  { k: "pct_consejo_en_receta", label: "Ratio de ventas mixtas", fmt: formatPct, pp: true, tickets: true },
  { k: "ratio_productos_libre_receta", label: "Productos libres por receta", fmt: dec2, tickets: true },
  { k: "ratio_consejo_para", label: "Ratio de consejo", fmt: formatPct, pp: true, tickets: true },
  { k: "consejo_eur", label: "Venta de consejo", fmt: formatMoney },
  { k: "stock_parado_eur", label: "Stock D vendido", fmt: formatMoney },
];

function KpiCard({ kpi, emp, media, hist }) {
  return (
    <div className="card p-4" data-testid={`ficha-kpi-${kpi.k}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{kpi.label}</div>
      <div className="font-display text-xl font-extrabold mt-0.5">{emp[kpi.k] == null ? "—" : kpi.fmt(emp[kpi.k])}</div>
      <div className="mt-1.5 pt-1.5 border-t border-slate-100 space-y-0.5">
        <Hist cur={emp[kpi.k]} hist={media[kpi.k]} fmt={(v) => `farmacia ${kpi.fmt(v)}`} pp={kpi.pp} className="!whitespace-normal" />
        {hist && <Hist cur={emp[kpi.k]} hist={hist[kpi.k]} fmt={(v) => `histórico ${kpi.fmt(v)}`} pp={kpi.pp} className="!whitespace-normal" />}
      </div>
    </div>
  );
}

export const FichaEmpleado = ({ rows, hasTickets, importId, history, evolution, staff }) => {
  const [sel, setSel] = useState(rows?.[0]?.vendedor || "");
  const [sending, setSending] = useState(false);
  const emp = (rows || []).find((r) => r.vendedor === sel) || rows?.[0];
  const media = useMemo(() => mediaFarmacia(rows || []), [rows]);
  const h = history?.by_employee?.[emp?.vendedor];
  const hist = h && h.n_meses > 1 ? h : null;
  const serie = (evolution?.empleados?.[emp?.vendedor] || []).map((m) => ({
    label: m.label,
    Facturación: m.facturacion,
    Parafarmacia: m.facturacion_parafarmacia,
    "Ticket medio": m.ticket_medio,
    "Ratio mixtas": +((m.pct_consejo_en_receta || 0) * 100).toFixed(1),
    "% parafarmacia": +((m.pct_parafarmacia || 0) * 100).toFixed(1),
  }));
  if (!emp) return null;
  const email = (staff || []).find((s) => s.vendedor === emp.vendedor)?.email;
  const enviar = async () => {
    setSending(true);
    try {
      const r = await api.post(`/sales/imports/${importId}/employee-report/send`, { vendedor: emp.vendedor });
      toast.success(`Informe enviado a ${r.data.to}${r.data.copia_titular ? " (con copia al titular)" : ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo enviar");
    } finally {
      setSending(false);
    }
  };
  return (
    <div data-testid="ficha-empleado">
      <div className="card p-4 mb-5 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Empleado</span>
        <select className="input" style={{ width: 220 }} value={emp.vendedor} onChange={(e) => setSel(e.target.value)} data-testid="ficha-employee-select">
          {rows.map((r) => <option key={r.vendedor} value={r.vendedor}>{(staff || []).find((s) => s.vendedor === r.vendedor)?.nombre || r.vendedor}</option>)}
        </select>
        <span className="text-xs text-slate-500">{hist ? `${hist.n_meses} meses en el histórico` : "Aún sin histórico (menos de 2 meses)"}</span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={enviar} disabled={!email || sending} title={email ? `Enviar a ${email}` : "Añade su email en Personal"}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 hover:bg-slate-900 hover:text-white transition-colors text-[11px] font-semibold disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-inherit"
                  data-testid="ficha-send-email-btn">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} {email ? "Enviar por email" : "Sin email"}
          </button>
          <EmployeeReportButton importId={importId} vendedor={emp.vendedor} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-6">
        {KPIS.filter((k) => !k.tickets || hasTickets).map((k) => <KpiCard key={k.k} kpi={k} emp={emp} media={media} hist={hist} />)}
      </div>

      {serie.length > 1 ? (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card p-5" data-testid="ficha-chart-facturacion">
            <h3 className="font-display text-lg font-bold mb-3">Facturación y ticket medio, mes a mes</h3>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={serie}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} unit="€" />
                <Tooltip formatter={(v, n) => (n === "Ticket medio" ? formatMoney2(v) : formatMoney(v))} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="l" dataKey="Facturación" fill="#0f172a" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="l" dataKey="Parafarmacia" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                <Line yAxisId="r" type="monotone" dataKey="Ticket medio" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="card p-5" data-testid="ficha-chart-ratios">
            <h3 className="font-display text-lg font-bold mb-3">Ventas mixtas y parafarmacia, mes a mes</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={serie}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="Ratio mixtas" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="% parafarmacia" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="card p-6 text-sm text-slate-500" data-testid="ficha-sin-evolucion">Cuando haya dos o más meses con ventas de {emp.vendedor} aquí verás su evolución.</div>
      )}
    </div>
  );
};
